/**
 * DirectionalIcon — lucide icon wrapper that auto-flips in RTL context.
 * Use for: arrow-right, arrow-left, chevron-right, chevron-left, etc.
 * @layer-1 — shared component, propagates rtl:-scale-x-100 automatically.
 */
import {
  ArrowRight,
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  RotateCw,
  ArrowRightLeft,
} from 'lucide-react';
import { useDirection } from './DirectionProvider';
import { cn } from '@/shared/lib/utils';

const iconMap = {
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'arrow-clockwise': RotateCw,
  'caret-right': ArrowRightLeft,
  'caret-left': ArrowRightLeft,
} as const;

export type DirectionalIconName = keyof typeof iconMap;

interface DirectionalIconProps {
  name: DirectionalIconName;
  className?: string;
  size?: number;
}

/**
 * Renders directional icon with automatic RTL flip.
 * Icon name from iconMap; rtl:-scale-x-100 applied in RTL context.
 */
export function DirectionalIcon({ name, className, size }: DirectionalIconProps) {
  const { isRTL } = useDirection();
  const Icon = iconMap[name];

  return (
    <Icon
      className={cn(
        isRTL && 'rtl:-scale-x-100',
        className
      )}
      size={size}
    />
  );
}
